import React from 'react'
import { Paper, createStyles, withStyles } from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteDocument } from 'paintscout'
import QuoteContextProvider from '../context/QuoteContextProvider'
import type { WisetackPromoResponse } from '../Wisetack'
import ArchivedItemsView from './views/ArchivedItemsView'
import NotesView from './views/NotesView'
import QuoteView from './views/QuoteView'
import WorkOrderView from './views/WorkOrderView'

/**
 * Max widths of the quote/work-order/invoice card — the `paper` Paper below.
 *
 * This, NOT `PRESENTATION_CONTENT_WIDTHS`, is the document's visible edge. The card sits
 * inside the presentation content column and is capped independently of it, so its rendered
 * width is `min(column, cap)` — anything that has to line up with the card must honour BOTH
 * contracts. Sizing to the column alone is what made #17051 fail QA three times.
 */
export const QUOTE_CARD_MAX_WIDTHS = {
  /** default (above `breakpoints.down('md')`, i.e. > 1213.95px) */
  default: 954,
  /** `breakpoints.down('md')` */
  md: 818
} as const

/**
 * Width of the fixed canvas the quote preview is scaled onto — the `Scaler fixedWidth` used by both
 * ScaledQuote components (this package's and the Send dialog's local copy).
 *
 * Percentage widths inside that canvas (e.g. `PRESENTATION_CONTENT_WIDTHS.sm`) resolve against this,
 * not the viewport, because the Scaler's `transform: scale()` makes it their containing block. Keep it
 * a shared constant so those percentages can still be reasoned about — and asserted — if it changes.
 */
export const QUOTE_PREVIEW_FRAME_WIDTH = 980

const styles = (theme: Theme) => {
  return createStyles({
    root: {
      marginBottom: 20
    },
    scalerWrapper: {},
    paper: {
      borderRadius: 2,
      // boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.25)',
      marginLeft: 'auto',
      marginRight: 'auto',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: QUOTE_CARD_MAX_WIDTHS.default,
      [theme.breakpoints.down('md')]: {
        maxWidth: QUOTE_CARD_MAX_WIDTHS.md
      },
      '@media print': {
        border: 'none !important'
      }
    },
    paperWrapper: {},
    content: {
      // padding: `${pxToRem(50)} ${pxToRem(72)}`
      padding: '30px 30px',
      [theme.breakpoints.down('md')]: {
        padding: '20px 20px'
      },
      '@media print': {
        padding: '0'
      }
    },
    headerWrapper: {},
    bidTableWrapper: {
      marginTop: theme.spacing(2)
    },
    optionsTableWrapper: {
      marginTop: theme.spacing(5)
    },
    optionsTableSubhead: {
      ...theme.typography.subtitle2,

      paddingTop: theme.spacing(),
      paddingBottom: theme.spacing(2)
    },
    termsWrapper: {
      marginTop: theme.spacing(5)
    }
  })
}

export type QuoteView =
  | 'quote'
  | 'work-order'
  | 'product-order-form'
  | 'archived-items'
  | 'activity'
  | 'details'
  | 'terms'
  | 'notes'
  | 'presentation'
  | 'payment-receipt'

export interface QuoteProps {
  view?: QuoteView
  // If you aren't rendering inside of a QuoteContextProvider, you can provide
  // the necessary props here to render without one
  quote?: QuoteDocument
  tableView?: 'area' | 'substrate'
  online?: boolean
  wisetackPromo?: WisetackPromoResponse
  inPresentation?: boolean

  onClick?: (event: React.MouseEvent) => any
  onClickCapture?: (event: React.MouseEvent) => any
}

type QuotePropsWithInjections = QuoteProps & WithStyles<typeof styles>

/**
 * Renders a quote
 *
 * It will use the quote from an existing QuoteContextProvider if there is one, but if not, it'll provide its
 * own using the `quote` prop
 */
function Quote(props: QuotePropsWithInjections) {
  const { classes, view, quote, tableView, onClick, onClickCapture, inPresentation, online = true } = props

  const content = (
    <Paper
      classes={{ root: [classes.paper, 'Quote-paper'].join(' ') }}
      onClick={onClick}
      onClickCapture={onClickCapture}
    >
      <div className={classes.content}>
        {view === 'quote' && <QuoteView inPresentation={inPresentation} online={online} />}
        {view === 'work-order' && <WorkOrderView />}
        {view === 'archived-items' && <ArchivedItemsView />}
        {view === 'notes' && <NotesView />}
      </div>
    </Paper>
  )

  if (quote) {
    return (
      <QuoteContextProvider quote={quote} tableView={tableView}>
        {content}
      </QuoteContextProvider>
    )
  } else {
    return <>{content}</>
  }
}

Quote.defaultProps = {
  view: 'quote' as QuoteView
}

export default withStyles(styles)(Quote)
